import React, { useMemo, useState } from 'react';
import OnboardingShell from '../../components/OnboardingShell';
import PhotoCapture from './PhotoCapture';
import VoiceCheck from './VoiceCheck';
import NetworkTest from './NetworkTest';

/**
 * Step 2 — Candidate verification. Runs the enabled sub-checks (photo → voice → network) in sequence;
 * each stores its artifact, then we advance. When the last completes, the step is done.
 */
const SUBS = [
  { key: 'photo', label: 'Photo', title: 'Capture a live photo', subtitle: 'Look at the camera and take a clear photo for identity verification.', Comp: PhotoCapture },
  { key: 'voice', label: 'Voice', title: 'Voice verification', subtitle: 'Read the passage aloud so we can verify your microphone and voice.', Comp: VoiceCheck },
  { key: 'network', label: 'Network', title: 'Network quality', subtitle: 'A quick connection test to make sure your interview runs smoothly.', Comp: NetworkTest },
];

const VerificationStep = ({ steps, completed, config, ids, onContinue }) => {
  const enabled = useMemo(() => {
    const v = (config && config.verify) || {};
    return SUBS.filter((s) => v[s.key]);
  }, [config]);
  const rec = (config && config.record) || {};
  const recorded = !!(rec.screen || rec.camera || rec.audio);   // badge only when something is actually recorded

  const [idx, setIdx] = useState(0);

  // nothing enabled → immediately satisfied
  if (!enabled.length) {
    return (
      <OnboardingShell steps={steps} completed={completed} activeKey="verification" recording={recorded}
        title="Candidate verification" subtitle="No verification is required for this interview.">
        <button type="button" onClick={onContinue}
          className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 px-6 py-3 text-sm font-semibold text-white">
          Continue
        </button>
      </OnboardingShell>
    );
  }

  const cur = enabled[Math.min(idx, enabled.length - 1)];
  const advance = () => { if (idx + 1 >= enabled.length) onContinue(); else setIdx(idx + 1); };
  const { Comp } = cur;

  return (
    <OnboardingShell
      steps={steps} completed={completed} activeKey="verification" recording={recorded}
      title={cur.title} subtitle={cur.subtitle}
    >
      {/* sub-step chips */}
      <div className="mb-5 flex flex-wrap gap-2">
        {enabled.map((s, i) => (
          <span key={s.key} className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium
            ${i < idx ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
              : i === idx ? 'bg-primary-100 text-primary-700 dark:bg-violet-500/20 dark:text-violet-200'
                : 'bg-slate-100 text-slate-400 dark:bg-white/5'}`}>
            {i < idx ? '✓' : i + 1} {s.label}
          </span>
        ))}
      </div>

      <Comp ids={ids} onDone={advance} />
    </OnboardingShell>
  );
};

export default VerificationStep;
